import { AdminApiError, type AdminClient, type StreakOverride } from '@apx/admin-sdk';
import { prettyGame } from '../games.js';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { streakSchema } from '@apx/integrator-config/schemas';
import { showToast } from '../toast.js';

export interface StreakPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
}

const DEFAULT_VALUE: StreakOverride = {
  enabled: false,
  bonusPerStep: 0.1,
  maxStreak: 5,
};

export async function renderStreakPage(
  root: HTMLElement,
  deps: StreakPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Streak Multiplier — ${prettyGame(deps.gameId)}</h1>
    <p class="page-sub">Per-(player, game) consecutive-win bonus: every successive same-category win adds <code>bonusPerStep</code> to the payout multiplier, capped at <code>maxStreak</code>. Off by default — flip <em>enabled</em> to opt in.</p>

    <div class="card">
      <h2>Knobs</h2>
      <div class="row" style="gap:24px">
        <label style="display:inline-flex;align-items:center;gap:6px"><input id="st-enabled" type="checkbox" /> Enabled</label>
        <label style="flex:1">Bonus per step (0..1)
          <input id="st-bonus" type="number" min="0" max="1" step="0.01" />
          <span class="muted" id="st-bonus-pct">— %</span>
        </label>
        <label style="flex:1">Max streak length (1..100)
          <input id="st-max" type="number" min="1" max="100" step="1" />
        </label>
      </div>
      <div class="row" style="margin-top:14px">
        <button id="st-save" class="primary">Propose change</button>
        <button id="st-reset" class="ghost">Reset to default (off)</button>
      </div>
      <p id="st-msg" class="muted"></p>
    </div>

    <div class="card">
      <h2>Effective multiplier preview</h2>
      <p class="muted" style="margin-top:-4px">Multiplier applied to the basePayout on the Nth consecutive same-category win.</p>
      <div id="st-preview" style="margin-top:8px"></div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="st-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const enabledEl = root.querySelector<HTMLInputElement>('#st-enabled')!;
  const bonusEl = root.querySelector<HTMLInputElement>('#st-bonus')!;
  const bonusPctEl = root.querySelector<HTMLElement>('#st-bonus-pct')!;
  const maxEl = root.querySelector<HTMLInputElement>('#st-max')!;
  const previewEl = root.querySelector<HTMLElement>('#st-preview')!;
  const msgEl = root.querySelector<HTMLElement>('#st-msg')!;

  function updatePreview(): void {
    const bonus = Number(bonusEl.value || 0);
    const max = Number(maxEl.value || 0);
    bonusPctEl.textContent = `${(bonus * 100).toFixed(1)} %`;
    const rows: string[] = [];
    for (let n = 0; n <= Math.min(max, 10); n++) {
      const mult = 1 + n * bonus;
      rows.push(
        `<tr><td>${n}</td><td>×${mult.toFixed(2)}</td><td>+${((mult - 1) * 100).toFixed(1)} %</td></tr>`,
      );
    }
    previewEl.innerHTML = `<table>
      <thead><tr><th>Prior streak length</th><th>Multiplier</th><th>Bonus</th></tr></thead>
      <tbody>${rows.join('')}</tbody>
    </table>`;
  }
  enabledEl.addEventListener('change', updatePreview);
  bonusEl.addEventListener('input', updatePreview);
  maxEl.addEventListener('input', updatePreview);

  function fillForm(value: StreakOverride): void {
    enabledEl.checked = value.enabled;
    bonusEl.value = String(value.bonusPerStep);
    maxEl.value = String(value.maxStreak);
    updatePreview();
  }

  const { value: current } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'streak');
  fillForm((current as StreakOverride | null) ?? DEFAULT_VALUE);

  root.querySelector<HTMLButtonElement>('#st-reset')!.addEventListener('click', () => {
    fillForm(DEFAULT_VALUE);
    msgEl.textContent = 'Reset to default (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#st-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const value: StreakOverride = {
      enabled: enabledEl.checked,
      bonusPerStep: Number(bonusEl.value),
      maxStreak: Math.round(Number(maxEl.value)),
    };
    const parsed = streakSchema.safeParse(value);
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'streak',
        parsed.data,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'streak' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#st-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th>Pending</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td><code>${JSON.stringify(a.pendingConfig)}</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          await refreshPending();
          const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'streak');
          fillForm((fresh.value as StreakOverride | null) ?? DEFAULT_VALUE);
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
  }

  await refreshPending();
}
